import './ScannerNamesList.css';
import React, { useState, useEffect } from 'react';
import { Card } from '../../../../../../components/Card/Card';
import GlobalProps from '../../../../../../components/globalProps';
import { useRemoteData } from '../../../../../../model/data';
import { getEventScannerNames, updateEventScannerNames } from '../../../../../../model/event';
import { SortableList } from '../../../../../../components/SortableList/SortableList';
import { Icon } from '../../../../../../components/Icon/Icon';
import { Line } from '../../../../../../components/Line/Line';
import { Modal } from '../../../../../../components/Modal/Modal';
import { ScannerNamesForm } from './ScannerNamesForm';
import { useConfirmation } from '../../../../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { t } from '../../../../../../locales/translate';
import { ScannerNameQR } from './ScannerNameQR';

interface Props extends GlobalProps {
  eventId: string;
}

export function ScannerNamesList({ eventId }: Props): JSX.Element {
  // Get event codes
  const [event, loading, error] = useRemoteData(
    async () => await getEventScannerNames(eventId),
    (db) => db.table('events').find(eventId)
  );

  const [displayAddModal, setDisplayAddModal] = useState(false);

  const [selectedName, setSelectedName] = useState<string | null>(null);

  const { confirm } = useConfirmation({
    title: t.model.scanner.delete,
    message: t.general.confirmDelete(`${t.model.scanner._name}: ${selectedName}`),
    action: async () => {
      if (event?.scannerNames) {
        await updateEventScannerNames(
          eventId,
          event?.scannerNames?.filter((item) => item !== selectedName)
        );
        setSelectedName(null);
      }
    },
    onClose: () => setSelectedName(null),
  });

  useEffect(() => {
    if (selectedName) {
      confirm();
    }
  }, [selectedName]);

  const items = [
    { name: 'default', actions: null },
    ...(event?.scannerNames?.map((item) => ({ name: item, actions: null })) || []),
  ];

  return (
    <section className="ticket-scanner__panel">
      <Card
        title={t.model.scanner._plural}
        actions={<Icon name="plus" tooltip={t.model.scanner.addNew} onClick={() => setDisplayAddModal(true)} />}
      >
        <SortableList
          className="scanner-names-list"
          items={items}
          loading={loading}
          error={error}
          expandable={() => true}
          expandedContent={(row) => event && <ScannerNameQR eventId={event.secureId} scannerName={row.name} />}
          columns={[
            {
              label: t.general.name,
              dataKey: 'name',
              cellRenderer: (row) => row.name || 'Default',
              expandedOnMount: true,
            },
            {
              label: t.general.actions,
              dataKey: 'actions',
              labelAlign: 'right',
              cellRenderer: (row) => {
                if (row.name === 'default') return;
                return (
                  <Line justify="right">
                    <Icon name="trash" tooltip={t.model.scanner.delete} onClick={() => setSelectedName(row.name)} />
                  </Line>
                );
              },
            },
          ]}
        />
      </Card>
      <Modal open={displayAddModal} title={t.model.scanner.addNew}>
        <ScannerNamesForm
          eventId={eventId}
          scannerNames={event?.scannerNames}
          onClose={() => setDisplayAddModal(false)}
        />
      </Modal>
    </section>
  );
}
